import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

const POLICY_VERSION = '1.0';
const EFFECTIVE_DATE = '17 May 2026';
const COMPANY = 'Rostery';
const CONTACT_EMAIL = 'privacy@skillwisedisability.com.au';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft size={15} /> Back to login
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
              <Shield size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Privacy Policy</h1>
              <p className="text-sm text-slate-500 mt-1">Version {POLICY_VERSION} · Effective {EFFECTIVE_DATE}</p>
            </div>
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">1. About This Policy</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              {COMPANY} ("we", "us", "our") operates Rostery, a disability care management platform used by
              NDIS-registered service providers. This policy explains how we collect, use, store, and protect
              personal information in accordance with the <strong>Privacy Act 1988 (Cth)</strong> and the
              <strong> Australian Privacy Principles (APPs)</strong>.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              We also comply with applicable state health privacy legislation, including the
              <strong> Health Records and Information Privacy Act 2002 (NSW)</strong> and the
              <strong> Health Records Act 2001 (Vic)</strong>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">2. What Information We Collect (APP 3 &amp; APP 5)</h2>
            <div className="space-y-2 text-sm text-slate-600">
              <p><strong>About NDIS participants (clients):</strong> name, date of birth, NDIS number, contact details, address, gender, disability information, care plans, goals, medications, incident reports, progress notes, and uploaded documents (e.g. NDIS plans, behaviour support plans).</p>
              <p><strong>About staff and carers:</strong> name, contact details, employment information, qualifications, NDIS Worker Screening clearance, pay group, tax file number (TFN — collected only for payroll purposes), bank account details.</p>
              <p><strong>About family portal users:</strong> name and email address.</p>
              <p>We collect only what is necessary to deliver disability support services and comply with NDIS registration requirements.</p>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">3. How We Use Your Information (APP 6)</h2>
            <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
              <li>Scheduling and coordinating disability support services</li>
              <li>Processing timesheets, invoices, and NDIS claims</li>
              <li>Maintaining clinical records (progress notes, medications, incidents)</li>
              <li>Compliance with NDIS Practice Standards and reporting obligations</li>
              <li>Staff payroll and workforce management</li>
              <li>Communicating with participants, families, and staff</li>
            </ul>
            <p className="text-sm text-slate-600">We do not use personal information for direct marketing.</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">4. Cross-Border Disclosure &amp; AI Processing (APP 8)</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Where a participant or provider has provided explicit consent, uploaded documents may be analysed
              by an artificial intelligence service operated by <strong>Anthropic, PBC</strong> (United States).
              This analysis extracts structured data from documents such as NDIS plans and care plans to
              pre-fill participant records. <strong>No document is sent to Anthropic without the participant's
              explicit opt-in consent</strong>, which can be granted or withdrawn at any time in the participant's
              profile settings.
            </p>
            <p className="text-sm text-slate-600">
              We have taken reasonable steps under APP 8.1 to ensure Anthropic's data handling meets an
              equivalent standard to the APPs through their enterprise data processing agreement.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">5. Security of Personal Information (APP 11)</h2>
            <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
              <li>All data transmitted over HTTPS (TLS 1.2+)</li>
              <li>Passwords hashed using bcrypt (never stored in plain text)</li>
              <li>Tax file numbers and bank details stored with encryption at rest</li>
              <li>Role-based access control — staff only access information relevant to their role</li>
              <li>Clinical data access is logged with timestamp, user identity, and action</li>
              <li>JWT tokens with 12-hour expiry; server-side revocation on logout</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">6. Data Retention (APP 11.2)</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We retain participant records for a minimum of <strong>7 years after the last service delivery</strong>
              as required by the NDIS Practice Standards r.9(1)(f). For participants who were minors at the time of
              service, records are retained until the person turns 25 (or 7 years, whichever is later).
              After the mandatory retention period, records are de-identified or destroyed.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">7. Access and Correction (APP 12 &amp; APP 13)</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              You have the right to access personal information we hold about you and to request corrections.
              To make an access or correction request, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">8. Complaints (APP 1.4)</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you believe we have breached the APPs, you may lodge a complaint with us first at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
              If you are not satisfied with our response, you may contact the{' '}
              <strong>Office of the Australian Information Commissioner (OAIC)</strong> at{' '}
              <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">www.oaic.gov.au</a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">9. Contact Us</h2>
            <div className="text-sm text-slate-600 space-y-1">
              <p><strong>Privacy Officer</strong></p>
              <p>{COMPANY}</p>
              <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a></p>
            </div>
          </section>

          <div className="pt-4 border-t border-slate-100 text-xs text-slate-400">
            Policy version {POLICY_VERSION} · Effective {EFFECTIVE_DATE} · This policy is reviewed annually.
          </div>
        </div>
      </div>
    </div>
  );
}
